/**
 * @file FooterColumnCard.tsx
 * @description Карточка колонки меню подвала (phase19.16, приёмка Р2а;
 * SB113, SB115; uiux-footer-columns §4.6, §4.7, §5): ⋮⋮ — перетащить
 * колонку, заголовок правится на месте, «⋯» колонки (Левее · Правее ·
 * Удалить колонку); автосписок категорий — серым со «Сколько показывать»;
 * пункты строками 56 с ⋮⋮ и «⋯» (перетаскиваются и между колонками);
 * пометка дубля ссылки платформы; пустая колонка — «не видна на сайте».
 */

import { useDroppable } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { FooterLayoutColumn } from "@chaiberi/shared";
import { GripVertical, Plus } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "../../ui/button";
import { RowActionsMenu } from "../../ui/RowActionsMenu";
import { StatusPill } from "../../ui/StatusPill";
import { footerColumnActions, footerItemActions, type FooterMenuHandlers } from "./footer-menu-actions";
import { FooterAutoList } from "./FooterAutoList";
import { FooterColumnTitle } from "./FooterColumnTitle";
import { MenuItemRow } from "./MenuItemRow";
import { columnDndId, columnDropId } from "../../../lib/site-view/footer-dnd";
import { FOOTER_DUPLICATE_NOTE } from "../../../lib/site-view/footer-columns";
import type { SiteMenuItem } from "../../../lib/site-view/types";
import { cn } from "@/lib/utils";

export interface FooterColumnCardProps {
  column: SiteMenuItem;
  /** Номер по порядку среди колонок меню (первая на сайте — «О магазине»). */
  index: number;
  info: FooterLayoutColumn<SiteMenuItem> | undefined;
  tree: SiteMenuItem[];
  handlers: FooterMenuHandlers;
  /** id пунктов-дублей ссылок платформы (SB115). */
  duplicates: ReadonlySet<string>;
  autoCategories: readonly string[] | null;
  highlighted: boolean;
  onAddItem: (columnId: string) => void;
  onRename: (column: SiteMenuItem, label: string | null) => Promise<boolean>;
  onLimit: (column: SiteMenuItem, limit: number | null) => void;
}

/** Область «бросьте сюда» пустой колонки: своя цель перетаскивания, высота от 64. */
function EmptyDropZone({ columnId, children }: { columnId: string; children: ReactNode }) {
  const drop = useDroppable({ id: columnDropId(columnId) });
  return (
    <div
      ref={drop.setNodeRef}
      data-drop-zone={columnId}
      className={cn("flex min-h-16 flex-col gap-2 rounded-lg border border-dashed p-3", drop.isOver ? "border-primary bg-primary/8" : "border-on-surface/30")}
    >
      {children}
    </div>
  );
}

export function FooterColumnCard(props: FooterColumnCardProps) {
  const { column, index, info, tree, handlers, duplicates, autoCategories, highlighted, onAddItem } = props;
  const sortable = useSortable({ id: columnDndId(column.id) });
  const auto = column.linkType === "CATALOG_CATEGORIES";
  const empty = info?.empty ?? column.children.length === 0;
  const number = `Колонка ${index + 2}`;
  const addItem = (
    <Button variant="outline" className="self-start" onClick={() => onAddItem(column.id)}>
      <Plus aria-hidden />
      Пункт в колонку
    </Button>
  );

  return (
    <section
      ref={sortable.setNodeRef}
      id={`footer-column-${column.id}`}
      aria-label={number}
      style={{ transform: CSS.Transform.toString(sortable.transform), transition: sortable.transition }}
      className={cn(
        "flex scroll-mt-4 flex-col gap-3 rounded-xl bg-surface-lowest p-3 [box-shadow:inset_0_0_0_1px_var(--border)]",
        highlighted && "ring-2 ring-primary",
        sortable.isDragging && "relative z-10 shadow-lg",
      )}
    >
      <div className="flex min-h-12 items-center gap-1">
        <button
          type="button"
          {...sortable.attributes}
          {...sortable.listeners}
          aria-label={`Перетащить колонку ${index + 2}`}
          className="inline-flex size-12 shrink-0 cursor-grab touch-none items-center justify-center rounded-md text-on-surface-variant outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <GripVertical aria-hidden className="size-4" />
        </button>
        <span className="type-card-title min-w-0 flex-1 truncate text-on-surface">
          {number}
          {empty && !auto ? " · пустая" : ""}
        </span>
        {column.isHidden ? <StatusPill tone="neutral">Скрыта</StatusPill> : null}
        <RowActionsMenu actions={footerColumnActions(tree, column, handlers)} title={number} size="compact" />
      </div>
      <FooterColumnTitle
        id={`footer-column-title-${column.id}`}
        value={column.label}
        required={auto}
        onSave={(label) => props.onRename(column, label)}
      />
      {auto ? (
        <FooterAutoList
          id={`footer-column-limit-${column.id}`}
          autoLimit={column.autoLimit}
          categories={autoCategories}
          onLimit={(limit) => props.onLimit(column, limit)}
        />
      ) : (
        <SortableContext items={column.children.map((c) => c.id)} strategy={verticalListSortingStrategy}>
          {column.children.length > 0 ? (
            <div className="flex flex-col divide-y divide-[var(--border)] overflow-hidden rounded-lg [box-shadow:inset_0_0_0_1px_var(--border)]">
              {column.children.map((item) => (
                <MenuItemRow
                  key={item.id}
                  item={item}
                  nested={false}
                  actions={footerItemActions(tree, item, handlers)}
                  notice={duplicates.has(item.id) ? `! ${FOOTER_DUPLICATE_NOTE}` : undefined}
                />
              ))}
            </div>
          ) : null}
          {column.children.length === 0 ? (
            <EmptyDropZone columnId={column.id}>
              <p className="type-body text-on-surface-variant">Пустая колонка не видна на сайте. Перетащите сюда пункт или добавьте новый.</p>
              <div className="flex flex-wrap gap-2">
                {addItem}
                <Button variant="outline" className="text-destructive" onClick={() => handlers.removeColumn(column)}>
                  Удалить колонку
                </Button>
              </div>
            </EmptyDropZone>
          ) : empty ? (
            <div className="flex flex-col gap-2">
              <p className="type-body text-on-surface-variant">Видимых пунктов нет — колонка не видна на сайте.</p>
              {addItem}
            </div>
          ) : (
            addItem
          )}
        </SortableContext>
      )}
    </section>
  );
}
